<template>
  <ModalBase :show-modal="!loading" @close-modal="close" unpadded modal-modal class="gk-premium-modal mini-premium">
    <template v-slot:maincontent>
      <div class="premium-modal__wrap">
        <!-- IMAGE -->
        <div class="premium-modal__cta-image">
          <div style="position: absolute; top: 1.5rem; left: 1.5rem;">
            <GkBadge
              :text="$gettext('Premium')"
              icon="fa-solid fa-crown"
              type="premium"
              variant="base"
              size="lg"
            />
          </div>
          <img :src="imageSrc" alt="">
        </div>
        <!-- CONTENT -->
        <div class="premium-modal__html-content-wrap" :class="{ 'os-overflown-content': !scrollEnd}">
          <!-- HEADER -->
          <div v-if="headerHtml" class="premium-modal__html-header" v-html="headerHtml" />

          <!-- MAIN -->
          <div class="content-wrap-shadow">
            <overlay-scrollbars ref="premiumModalScrollbar" :options="scrollBarsOptions">
              <div class="premium-modal__html-content">
                <div v-if="mainHtml" ref="mainHtmlContainer" class="content" v-html="mainHtml" />
                <PremiumModalDefaultContent v-else />
              </div>
            </overlay-scrollbars>
            <div class="overflown-content-shadow-wrap">
              <div class="overflown-content-shadow"></div>
            </div>
          </div>

          <!-- FOOTER -->
          <div class="premium-modal__html-footer">
            <!-- <div v-html="priceHtml" /> -->
            <div v-if="footerHtml" v-html="footerHtml" />
            <div v-else>
              <GekkoButton
                data-analytics-click="Subscription modal v3: upgrade"
                :href="Urls['subscriptions:manage']()"
                :text="$gettext('Upgrade now')"
                class="w-100"
                size="xl"
              />
            </div>
            <div class="pt-3">
              <GekkoButton
                data-analytics-click="Subscription modal v3: pricing"
                :href="Urls['pages']('pricing')"
                :text="$gettext('See all Premium features')"
                target="_blank"
                variant="link"
                class="w-100 p-0"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </ModalBase>  
</template>
<script>
import { mapGetters, mapMutations } from "vuex"

import PremiumService from "@/store/services/premium.service.js";
import GekkoButton from "@/components/GekkoButton";
import ModalBase from "@/components/base_components/ModalBase";
import TitleBar from "@/components/TitleBar";
import PremiumModalDefaultContent from "@/components/ui/PremiumModalDefaultContent"

export default {
  name: "PremiumModal",
  components: { 
    GekkoButton,
    ModalBase,
    TitleBar,
    PremiumModalDefaultContent
  },
  data () {
    return {
      modalHtml: null,
      headerHtml: null,
      mainHtml: null,
      priceHtml: null,
      footerHtml: null,
      image: null,
      loading: true,
      scrollEnd: true,
      scrollBarsOptions: {
        scrollbars: { autoHide: 'never' },
        overflowBehavior: { y: 'scroll', x: 'hidden' },
        callbacks : {
          onScroll: this.handleScroll
        }
      },
      defaultImage: '/static/img/default-premium-modal.png',
    }
  },
  computed: {
    ...mapGetters ({
      getPremiumModalCode: "getPremiumModalCode",
    }),
    imageSrc () {
      return (this.image) ? this.image : this.defaultImage
    },
  },
  watch: {
    getPremiumModalCode () {
      this.setupData()
    }
  },
  mounted () {
    document.body.classList.add('modal-on')
    this.loading = true
    this.setupData()
  },
  methods: {
    ...mapMutations ({
      togglePremiumModal: "togglePremiumModal",
    }),
    setupData () {
      PremiumService.list({ code: this.getPremiumModalCode }).then((response) => {
        this.modalHtml = response.data[0].content
        const { headerContent, mainContent, priceContent, footerContent } = this.processHtml(this.modalHtml)
        this.headerHtml = headerContent
        this.mainHtml = mainContent
        this.priceHtml = priceContent
        this.footerHtml = footerContent
        this.image = response.data[0].image
      }).catch((error) => {
        console.log('error loading premium modal', error)
        this.resetModalData()
      }).finally(() => {
        this.loading = false
        this.$nextTick(() => {
          this.osInstance = this.$refs.premiumModalScrollbar.osInstance()
          this.handleScroll() // check if content shadow should be displayed at init
          this.mountComponents('gk-premium-badge', 'GkBadge', 'mainHtmlContainer')
        })
      })
    },
    mountComponents (placeholder_name, component_name, html_container_ref) {
      if (!this.$refs[html_container_ref]) return
      const placeholders = this.$refs[html_container_ref].querySelectorAll(placeholder_name);
      const ComponentConstructor = this.$options.components[component_name];
      placeholders.forEach((placeholder) => {
        const propsData = {}
        for (const attr of placeholder.attributes) {
          let val = attr.value
          try {
            val = JSON.parse(val)
          } catch (e) {
            // keep as string if not valid JSON
          }
          propsData[attr.name] = val
        }
        const ComponentInstance = new ComponentConstructor({
          propsData,
          store: this.$store,
        })
        ComponentInstance.$mount();
        placeholder.replaceWith(ComponentInstance.$el);
      })
    },
    close () {
      this.togglePremiumModal(null)
    },
    processHtml(htmlString) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');

      const priceSection = doc.querySelector('.popup-price-section');
      let priceContent = '';
      if (priceSection) {
        priceContent = priceSection.outerHTML;
        priceSection.remove();
      }

      // Extract and remove .header
      const header = doc.querySelector('.header');
      let headerContent = '';
      if (header) {
        headerContent = header.outerHTML;
        header.remove();
      }
      // Extract and remove .footer
      const footer = doc.querySelector('.footer');
      let footerContent = '';
      if (footer) {
        footerContent = footer.outerHTML;
        footer.remove();
      }

      // Remaining HTML
      const remainingHtml = doc.body.innerHTML;

      return {
        headerContent,
        mainContent: remainingHtml,
        priceContent,
        footerContent
      };
    },
    resetModalData () {
      this.modalHtml = null
      this.headerHtml = null
      this.mainHtml = null
      this.priceHtml = null
      this.footerHtml = null
      this.image = null
    },
    handleScroll () {
      if (this.osInstance && this.$refs.premiumModalScrollbar.$el.classList.contains('os-host-overflow-y')) {
        const scrollEndThreshold = .99
        this.scrollEnd = (this.osInstance.scroll().ratio.y >= scrollEndThreshold)
      } else {
        this.scrollEnd = true
      }
    }
  },
};
</script>
<style lang="scss">

</style>
